// DeskSwitch — which desk's books a screen shows.
//
// 5 Sept 2026: the operator asked "where to go for india books". There was
// nowhere — every screen read DubaiOTC, and the India banks (29 rails under
// treasury:indiaotc:*) were reachable only through the switch Move money had
// gained that morning. This is that switch, shared, so Treasury, Movements and
// Move money all say the same thing and carry it in the URL (?entity=) so a
// link can open a screen on the India desk directly.
//
// ONE DESK (operator, 26 Sept 2026, on Treasury's live switch: "remove
// indiaotc from here as well" — the follow-through on 25 Sept's "cut indiaotc
// … we have moved every asset", 2ce2119: IndiaOTC now holds no treasury and no
// assets). Every screen this switch appears on — Treasury, Movements, Record
// money, Convert, Treasury rebalance, Record between parties — is about
// desk ACCOUNTS, and IndiaOTC has none left; a switch offering it would open
// onto rows that are all zero. `Desk` narrows to the one value with anything
// to switch to, so every screen built on it is DubaiOTC-only by the type
// system, not by a UI restriction a stale link could slip past — the same
// reasoning that made RecordBetweenParties.tsx's own fix ignore
// `?entity=indiaotc` rather than honour it (82e5858). If IndiaOTC ever trades
// again, widening this one line is the whole way back; nothing downstream
// needs rebuilding, only tsc's own errors need clearing, each one a real
// place that assumed IndiaOTC could still be picked.
//
// Rendered only when the signed-in admin can see more than one desk — which,
// with one desk left in DESKS, is never; the component still checks `scope`
// rather than hard-return null, so a second desk re-opens this for free.
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

export type Desk = 'dubaiotc';
const DESKS: Desk[] = ['dubaiotc'];
export const DESK_LABEL: Record<Desk, string> = { dubaiotc: 'DubaiOTC' };
const isDesk = (v: unknown): v is Desk => v === 'dubaiotc';

/** The desk from the URL (?entity=), DubaiOTC when absent; setting it rewrites
 *  the URL so the choice survives a reload and can be shared as a link. */
export function useDesk(): [Desk, (d: Desk) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('entity');
  const desk: Desk = isDesk(raw) ? raw : 'dubaiotc';
  const set = (d: Desk) => {
    const next = new URLSearchParams(params);
    if (d === 'dubaiotc') next.delete('entity'); else next.set('entity', d);
    setParams(next, { replace: true });
  };
  return [desk, set];
}

export function DeskSwitch({ value, onChange, className = '' }: { value: Desk; onChange: (d: Desk) => void; className?: string }) {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 300_000 });
  const scope = (me.data?.entityScope ?? ['dubaiotc']).filter(isDesk);
  if (scope.length < 2) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {DESKS.filter((d) => scope.includes(d)).map((d) => (
        <button
          key={d} type="button" onClick={() => onChange(d)}
          className={`chip text-body px-3 py-1.5 ${d === value ? 'chip-on' : ''}`}
        >{DESK_LABEL[d]}</button>
      ))}
    </div>
  );
}
